import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { 
  Check as CheckIcon,
  School,
  Business,
  Group,
  Psychology,
  EmojiPeople,
  VerifiedUser,
  EventNote,
  Diversity3,
  WorkspacePremium,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import PageHeader from '../../components/PageHeader';

const Workshops = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language].workshopsPage;

  const listItemStyle = {
    mb: 1.5,
    display: 'flex',
    alignItems: 'flex-start'
  };

  const checkIconStyle = {
    color: 'primary.main',
    mr: 1.5,
    mt: 0.5,
    fontSize: '1.2rem'
  };

  // Map icons to workshop areas
  const offerIcons = [
    <Psychology sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <School sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <Business sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <Group sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <EventNote sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <Diversity3 sx={{ color: 'primary.main', fontSize: '1.8rem' }} />
  ];

  // Workshop benefits
  const benefits = [
    {
      title: language === 'nl' ? 'Interactieve aanpak' : 'Interactive approach',
      description: language === 'nl' 
        ? 'Deelnemers worden actief betrokken door oefeningen en discussies die directe toepassing bevorderen.'
        : 'Participants are actively involved through exercises and discussions that promote direct application.',
      icon: <Group color="primary" sx={{ mr: 1.5, fontSize: '1.8rem' }} />
    },
    {
      title: language === 'nl' ? 'Op maat gemaakte inhoud' : 'Customized content',
      description: language === 'nl' 
        ? 'Workshops worden afgestemd op de specifieke behoeften en doelen van jouw team of organisatie.'
        : 'Workshops are tailored to the specific needs and goals of your team or organization.',
      icon: <WorkspacePremium color="primary" sx={{ mr: 1.5, fontSize: '1.8rem' }} />
    },
    {
      title: language === 'nl' ? 'Blijvende impact' : 'Lasting impact',
      description: language === 'nl' 
        ? 'Praktische tools en technieken die deelnemers kunnen blijven toepassen in hun dagelijkse werk en leven.'
        : 'Practical tools and techniques that participants can continue to apply in their daily work and life.',
      icon: <VerifiedUser color="primary" sx={{ mr: 1.5, fontSize: '1.8rem' }} />
    }
  ];

  return (
    <>
      <PageHeader title={t.pageTitle} subtitle={t.pageSubtitle} />
      
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography variant="body1" paragraph sx={{ mb: 6, fontSize: '1.1rem', lineHeight: 1.7, textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
          {t.groupTrainingDescription}
        </Typography>

        {/* "Why Me" Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => navigate('/waarom-ik#host')}
            startIcon={<EmojiPeople sx={{ color: 'white' }} />}
            sx={{ 
              px: 4, 
              py: 1.2,
              color: 'white',
              backgroundColor: 'secondary.main',
              '&:hover': {
                backgroundColor: 'secondary.dark'
              }
            }}
          >
            {language === 'nl' ? 'Waarom mij kiezen als trainer?' : 'Why choose me as a trainer?'}
          </Button>
        </Box>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Workshop Areas Grid */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: 3, 
              bgcolor: 'background.paper', 
              borderRadius: 2, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              boxShadow: 1,
              border: '1px solid rgba(0,0,0,0.08)'
            }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 3, fontWeight: 600 }}>
                {language === 'nl' ? 'Workshop onderwerpen' : 'Workshop topics'}
              </Typography>
              <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                {/* Personal Development Card */}
                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{ 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    textAlign: 'center',
                    height: '100%',
                    border: '1px solid rgba(0,0,0,0.05)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 2
                    }
                  }}>
                    <Box sx={{ mb: 1.5 }}>
                      {offerIcons[0]}
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {t.column1Title}
                    </Typography>
                  </Paper>
                </Grid>
                
                {/* Professional Development Card */}
                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{ 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    textAlign: 'center',
                    height: '100%',
                    border: '1px solid rgba(0,0,0,0.05)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 2
                    }
                  }}>
                    <Box sx={{ mb: 1.5 }}>
                      {offerIcons[1]}
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {t.column2Title}
                    </Typography>
                  </Paper>
                </Grid>
                
                {/* Custom Training Card */}
                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{ 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    textAlign: 'center',
                    height: '100%',
                    border: '1px solid rgba(0,0,0,0.05)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 2
                    }
                  }}>
                    <Box sx={{ mb: 1.5 }}>
                      {offerIcons[2]}
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {t.column3Title}
                    </Typography>
                  </Paper>
                </Grid>
                
                {/* Team Building Card */}
                <Grid item xs={12} sm={6}>
                  <Paper elevation={0} sx={{ 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    textAlign: 'center',
                    height: '100%',
                    border: '1px solid rgba(0,0,0,0.05)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 2
                    }
                  }}>
                    <Box sx={{ mb: 1.5 }}>
                      {offerIcons[3]}
                    </Box>
                    <Typography variant="body1" fontWeight={500}>
                      {language === 'nl' ? 'Teambuilding' : 'Team Building'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Customized Training Section */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: 3, 
              bgcolor: 'background.paper', 
              borderRadius: 2, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              boxShadow: 1,
              border: '1px solid rgba(0,0,0,0.08)'
            }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2, fontWeight: 600 }}>
                {t.column3Title}
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                {t.column3Description}
              </Typography>
              <List sx={{ pl: 0, flexGrow: 1 }}>
                {t.column3Items.map((item, index) => (
                  <ListItem key={index} disablePadding sx={listItemStyle}>
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      <CheckIcon sx={checkIconStyle} />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>
        </Grid>
        
        {/* Benefits Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" component="h2" align="center" sx={{ mb: 4, fontWeight: 600 }}>
            {language === 'nl' ? 'Voordelen van mijn workshops' : 'Benefits of my workshops'}
          </Typography>
          
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', boxShadow: 2 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {benefit.icon}
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {benefit.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2">
                      {benefit.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Workshop Content Detail Section */}
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={4}>
            {/* Personal Development Column */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ 
                p: 3, 
                bgcolor: 'background.paper', 
                borderRadius: 2,
                boxShadow: 1,
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2, fontWeight: 600, textAlign: 'center' }}>
                  {t.column1Title}
                </Typography>
                <List sx={{ pl: 0 }}>
                  {t.column1Items.map((item, index) => (
                    <ListItem key={index} disablePadding sx={listItemStyle}>
                      <ListItemIcon sx={{ minWidth: 'auto' }}>
                        <CheckIcon sx={checkIconStyle} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Professional Development Column */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ 
                p: 3, 
                bgcolor: 'background.paper', 
                borderRadius: 2,
                boxShadow: 1,
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2, fontWeight: 600, textAlign: 'center' }}>
                  {t.column2Title}
                </Typography>
                <List sx={{ pl: 0 }}>
                  {t.column2Items.map((item, index) => (
                    <ListItem key={index} disablePadding sx={listItemStyle}>
                      <ListItemIcon sx={{ minWidth: 'auto' }}>
                        <CheckIcon sx={checkIconStyle} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Pricing Section */}
        <Box sx={{ mb: 6, textAlign: 'center', p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
            {t.pricingTitle}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 3 }}>
            {t.pricingDescription}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/contact')}
          >
            {language === 'nl' ? 'Vraag offerte aan' : 'Request quote'}
          </Button>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* "Why choose me" funnel section */}
        <Box sx={{ mb: 6, p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
            {language === 'nl' ? 'Waarom mij kiezen als trainer?' : 'Why choose me as your trainer?'}
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <School color="primary" sx={{ fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {language === 'nl' ? 'Academische achtergrond' : 'Academic background'}
                </Typography>
                <Typography variant="body2">
                  {language === 'nl' 
                    ? 'Als universitair opgeleide professional breng ik diepgaande kennis en wetenschappelijk onderbouwde methoden.'
                    : 'As a university-educated professional, I bring deep knowledge and scientifically supported methods.'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Psychology color="primary" sx={{ fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {language === 'nl' ? 'Praktijkervaring' : 'Practical experience'}
                </Typography>
                <Typography variant="body2">
                  {language === 'nl' 
                    ? 'Jarenlange ervaring in het begeleiden van groepen en teams, met bewezen resultaten en tevreden deelnemers.'
                    : 'Years of experience in guiding groups and teams, with proven results and satisfied participants.'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Diversity3 color="primary" sx={{ fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {language === 'nl' ? 'Persoonlijke aanpak' : 'Personal approach'}
                </Typography>
                <Typography variant="body2">
                  {language === 'nl' 
                    ? 'Ik ontwikkel workshops die perfect aansluiten bij de specifieke behoeften en doelstellingen van uw team of organisatie.'
                    : 'I develop workshops that perfectly align with the specific needs and objectives of your team or organization.'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/waarom-ik#host')}
              sx={{ px: 3, py: 1 }}
            >
              {language === 'nl' ? 'Lees meer over mijn aanpak' : 'Read more about my approach'}
            </Button>
          </Box>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mb: 4, p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
            {t.ctaTitle}
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            {t.ctaDescription}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => navigate('/contact', { state: { subject: t.pageTitle } })}
              sx={{ px: 4, py: 1.5 }}
            >
              {t.ctaButton}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => navigate('/waarom-ik')}
              sx={{ px: 4, py: 1.5 }}
            >
              {language === 'nl' ? 'Meer over mij' : 'More about me'}
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Workshops; 